import { Link } from '@remix-run/react';

import type { ProfileCompletionTarget } from '~/utils/profile-completeness';

interface ProfileCompletenessBannerProps {
  percent: number;
  target: ProfileCompletionTarget | null;
}

// Safety net only: getProfileCompletionTarget mirrors the percent items, so
// target is non-null whenever percent < 100.
const FALLBACK_TARGET: ProfileCompletionTarget = {
  label: 'Complete my profile',
  url: '/onboarding?mode=full',
};

// Deliberately not dismissable (product decision, 2026-07-28): the banner's
// whole job is to keep nudging until the profile is complete; it disappears
// on its own at 100%.
export function ProfileCompletenessBanner({ percent, target }: ProfileCompletenessBannerProps) {
  if (percent >= 100) {
    return null;
  }

  const { label, url } = target ?? FALLBACK_TARGET;

  return (
    <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50 p-4">
      <p className="text-sm font-semibold text-purple-900">Your profile is {percent}% complete.</p>
      <p className="mt-1 text-sm text-purple-800">
        A complete profile helps colleagues find you and send referrals your way.
      </p>
      <Link to={url} aria-label={label} className="mt-3 block">
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-purple-100"
          role="progressbar"
          aria-label="Profile completeness"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="h-full rounded-full bg-purple-600" style={{ width: `${percent}%` }} />
        </div>
      </Link>
      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to={url}
          className="rounded-md border border-purple-300 bg-white px-3 py-1.5 text-sm font-medium text-purple-900 hover:bg-purple-100"
        >
          {label}
        </Link>
      </div>
    </div>
  );
}
